<!--@format-->
<template>
    <div class="mdf-assign-users-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="description">{{ description }}</div>
                <div v-if="loading" class="loading-card">
                    <loading-screen-svg />
                </div>
                <div v-else class="input-container">
                    <agency-users-search
                        :options="getOptions()"
                        :selected="assignedUsers"
                        @assign="assignUser($event)"
                        @remove="removeUser($event)"
                    />
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button :loading="saving" @click="handleSave()"> Save </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AgencyUsersSearch = () => import('@/components/client/agency-users-search.vue');
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';
import { differenceBy } from 'lodash';
import { AccountsService } from '@/scripts/services/mdf/accounts-service';
import { AgencyManagementService } from '@/scripts/services/mdf/agency/agency-management-service';

export default {
    name: 'mdf-assign-users-widget',

    components: {
        'agency-users-search': AgencyUsersSearch,
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: true,

            saving: false,

            agencyAccountId: this.widget.agencyAccountId,

            availableUsers: [],

            assignedUsers: [],

            clientAccountId: this.widget.clientAccountId,

            mdfId: this.widget.mdfId,
        };
    },

    computed: {
        title() {
            return this.widget.assignedUsers.label;
        },

        description() {
            return 'Select one or more account managers for this partner';
        },

        accountManagerRoleId() {
            return 16;
        },

        fundsPayerRoleId() {
            return 14;
        },
    },

    created() {
        this.getUsers();
        this.assignedUsers = [...this.widget.assignedUsers.users];
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getOptions() {
            return differenceBy(this.availableUsers, this.assignedUsers, 'id');
        },

        handleSave() {
            this.saving = true;
            this.save()
                .then(() => {
                    this.$helpers.showSuccessNotification(`${this.title} saved successfully.`);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to save changes.', { error });
                })
                .finally(() => {
                    this.saving = false;
                    EventBus.$emit('mdf-management:update');
                    EventBus.$emit('agency-mdf-partners:update');
                    this.closeWidget();
                });
        },

        save() {
            if (this.clientAccountId) {
                return this.assignParticipantsToPartner();
            }
            return this.assignParticipantsToRequest();
        },

        assignParticipantsToPartner() {
            const mdfParticipants = this.assignedUsers.map(({ id, name, profilePicture }) => ({
                ClientAccountId: this.clientAccountId,
                ParticipantId: id,
                ParticipantType: 'Manager',
                Name: name,
                ProfileImageUrl: profilePicture,
            }));

            return AgencyManagementService.assignMDFUserToPartner({
                accountId: this.agencyAccountId,
                payload: {
                    ClientAccountId: this.clientAccountId,
                    Participants: mdfParticipants,
                },
            });
        },

        assignParticipantsToRequest() {
            const isManagerRequest = this.title === 'Account Manager';
            const mdfParticipants = this.assignedUsers.map(({ id, name, profilePicture }) => ({
                ParticipantId: id,
                ParticipantType: isManagerRequest ? 'Manager' : 'Approver',
                Name: name,
                ProfileImageUrl: profilePicture,
            }));

            const payload = {
                accountId: this.agencyAccountId,
                fundRequestId: this.mdfId,
                payload: {
                    Participants: mdfParticipants,
                },
            };

            if (isManagerRequest) {
                return AgencyManagementService.assignMDFRequestManager(payload);
            }
            return AgencyManagementService.assignMDFUserRequestApprover(payload);
        },

        getUsers() {
            this.loading = true;

            AccountsService.getAgency(this.agencyAccountId)
                .then(({ data: { data } }) => {
                    const roleId =
                        this.title === 'Account Manager'
                            ? this.accountManagerRoleId
                            : this.fundsPayerRoleId;
                    const filter = `$filter=AgencyId eq ${data.Id} and Roles/any(r: r/RoleId eq '${roleId}')`;
                    return this.$api.get(`/service/Users?${filter}`);
                })
                .then((response) => {
                    this.availableUsers = response.data.value.map(this.mapAgencyUserToOption);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while fetching users.', {
                        error,
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        mapAgencyUserToOption(user) {
            return {
                id: user.Id,
                name: `${user.FirstName} ${user.LastName}`,
                profilePicture: user.ProfileImageUrl,
                title: user.Title,
            };
        },

        assignUser(userToAssign) {
            this.assignedUsers.push(userToAssign);
        },

        removeUser(userToRemove) {
            this.assignedUsers = this.assignedUsers.filter(({ id }) => id !== userToRemove.id);
        },
    },
};
</script>

<style>
.mdf-assign-users-widget .description {
    font: normal 500 16px Roboto;
    margin-bottom: 8px;
}

.mdf-assign-users-widget .app-input {
    margin-bottom: 1em;
}

.mdf-assign-users-widget .assigned-users {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
}

.mdf-assign-users-widget .assigned-users .user-chips {
    align-items: center;
    border: 1px solid #cac6cb;
    border-radius: 15px;
    display: flex;
    font-size: 12px;
    gap: 8px;
    padding: 3px 10px;
}

.mdf-assign-users-widget .assigned-users .user-chips img {
    border-radius: 50%;
    height: 20px;
    width: 20px;
}

.mdf-assign-users-widget .assigned-users .user-chips svg {
    cursor: pointer;
    margin-left: 12px;
    width: 10px;
}

.mdf-assign-users-widget .cancel-btn {
    background: transparent;
    border: none;
    color: #877d88;
}

.mdf-assign-users-widget .cancel-btn:hover {
    background: #e9e9ef;
}
</style>
